/* eslint-disable no-console */
// Централизованная система мониторинга безопасности для критичных событий
// Следует принципам YAGNI - только необходимые события, простая реализация

export type SecurityEvent = {
  type: SecurityEventType;
  severity: 'low' | 'medium' | 'high' | 'critical';
  message: string;
  userId?: string;
  email?: string;
  ip?: string;
  userAgent?: string;
  timestamp: Date;
  metadata?: Record<string, unknown>;
}

export enum SecurityEventType {
  // Аутентификация
  LOGIN_FAILED = 'login_failed',
  LOGIN_SUCCESS = 'login_success',
  MULTIPLE_LOGIN_FAILURES = 'multiple_login_failures',
  RATE_LIMIT_EXCEEDED = 'rate_limit_exceeded',
  
  // Авторизация
  UNAUTHORIZED_ACCESS = 'unauthorized_access',
  PRIVILEGE_ESCALATION = 'privilege_escalation',
  
  // Подозрительная активность
  SUSPICIOUS_PATTERN = 'suspicious_pattern',
  DATA_BREACH_ATTEMPT = 'data_breach_attempt',
  
  // Системные события
  SECURITY_CONFIG_CHANGE = 'security_config_change',
  CRITICAL_ERROR = 'critical_error'
}

class SecurityMonitoringService {
  private events: SecurityEvent[] = [];
  private readonly MAX_EVENTS = 1000; // Ограничение памяти
  private readonly ALERT_THRESHOLDS = {
    LOGIN_FAILED: 5, // 5 неудачных попыток за период
    RATE_LIMIT_EXCEEDED: 3, // 3 превышения лимита
    UNAUTHORIZED_ACCESS: 1 // Любая попытка несанкционированного доступа
  };

  /**
   * Логирование события безопасности
   */
  logSecurityEvent(event: Omit<SecurityEvent, 'timestamp'>): void {
    const fullEvent: SecurityEvent = {
      ...event,
      timestamp: new Date()
    };

    // Добавляем в локальный массив
    this.events.push(fullEvent);
    
    // Ограничиваем размер массива
    if (this.events.length > this.MAX_EVENTS) {
      this.events = this.events.slice(-this.MAX_EVENTS);
    }

    // Логируем в консоль с соответствующим уровнем
    this.logToConsole(fullEvent);

    // Проверяем на необходимость алерта
    this.checkForAlerts(fullEvent);

    // В продакшене здесь можно добавить отправку в внешние системы мониторинга
    // например, Sentry, LogRocket, или собственный сервис
  }

  /**
   * Логирование неудачной попытки входа
   */
  logFailedLogin(email: string, error: string, metadata?: Record<string, unknown>): void {
    this.logSecurityEvent({
      type: SecurityEventType.LOGIN_FAILED,
      severity: 'medium',
      message: `Failed login attempt for email: ${email}`,
      email,
      metadata: { error, ...metadata }
    });
  }

  /**
   * Логирование успешного входа
   */
  logSuccessfulLogin(userId: string, email: string, metadata?: Record<string, unknown>): void {
    this.logSecurityEvent({
      type: SecurityEventType.LOGIN_SUCCESS,
      severity: 'low',
      message: `Successful login for user: ${email}`,
      userId,
      email,
      metadata
    });
  }

  /**
   * Логирование превышения rate limit
   */
  logRateLimitExceeded(identifier: string, action: string, metadata?: Record<string, unknown>): void {
    this.logSecurityEvent({
      type: SecurityEventType.RATE_LIMIT_EXCEEDED,
      severity: 'high',
      message: `Rate limit exceeded for ${action} by ${identifier}`,
      metadata: { identifier, action, ...metadata }
    });
  }

  /**
   * Логирование попытки несанкционированного доступа
   */
  logUnauthorizedAccess(userId: string, resource: string, metadata?: Record<string, unknown>): void {
    this.logSecurityEvent({
      type: SecurityEventType.UNAUTHORIZED_ACCESS,
      severity: 'high',
      message: `Unauthorized access attempt to ${resource} by user ${userId}`,
      userId,
      metadata: { resource, ...metadata }
    });
  }

  /**
   * Логирование критичной ошибки системы
   */
  logCriticalError(error: string, context: string, metadata?: Record<string, unknown>): void {
    this.logSecurityEvent({
      type: SecurityEventType.CRITICAL_ERROR,
      severity: 'critical',
      message: `Critical system error in ${context}: ${error}`,
      metadata: { context, error, ...metadata }
    });
  }

  /**
   * Получение последних событий безопасности
   */
  getRecentEvents(limit: number = 50): SecurityEvent[] {
    return this.events.slice(-limit).reverse();
  }

  /**
   * Получение событий по типу
   */
  getEventsByType(type: SecurityEventType, limit: number = 50): SecurityEvent[] {
    return this.events
      .filter(event => event.type === type)
      .slice(-limit)
      .reverse();
  }

  /**
   * Получение критичных событий
   */
  getCriticalEvents(limit: number = 20): SecurityEvent[] {
    return this.events
      .filter(event => event.severity === 'critical' || event.severity === 'high')
      .slice(-limit)
      .reverse();
  }

  /**
   * Логирование в консоль с соответствующим уровнем
   */
  private logToConsole(event: SecurityEvent): void {
    const logMessage = `[SECURITY] ${event.type.toUpperCase()}: ${event.message}`;
    const logData = {
      severity: event.severity,
      timestamp: event.timestamp.toISOString(),
      userId: event.userId,
      email: event.email,
      metadata: event.metadata
    };

    switch (event.severity) {
      case 'critical':
        console.error('🚨', logMessage, logData);
        break;
      case 'high':
        console.error('⚠️', logMessage, logData);
        break;
      case 'medium':
        console.warn('⚡', logMessage, logData);
        break;
      case 'low':
        console.log('ℹ️', logMessage, logData);
        break;
    }
  }

  /**
   * Проверка на необходимость алертов
   */
  private checkForAlerts(event: SecurityEvent): void {
    const recentEvents = this.getRecentEventsInTimeWindow(event.type, 15 * 60 * 1000); // 15 минут
    
    // Проверяем только для событий, у которых есть пороговые значения
    const threshold = this.ALERT_THRESHOLDS[event.type];
    if (threshold && recentEvents.length >= threshold) {
      this.triggerAlert(event.type, recentEvents.length, threshold);
    }

    // Немедленные алерты для критичных событий
    if (event.severity === 'critical') {
      this.triggerAlert(event.type, 1, 1);
    }
  }

  /**
   * Получение событий в временном окне
   */
  private getRecentEventsInTimeWindow(type: SecurityEventType, windowMs: number): SecurityEvent[] {
    const now = Date.now();
    return this.events.filter(event => 
      event.type === type && 
      (now - event.timestamp.getTime()) <= windowMs
    );
  }

  /**
   * Триггер алерта
   */
  private triggerAlert(eventType: SecurityEventType, count: number, threshold: number): void {
    const alertMessage = `🚨 SECURITY ALERT: ${eventType} occurred ${count} times (threshold: ${threshold})`;
    console.error(alertMessage);
    
    // В продакшене здесь можно добавить:
    // - Отправку уведомлений администраторам
    // - Интеграцию с системами алертинга (PagerDuty, Slack, etc.)
    // - Автоматические действия (блокировка IP, временная блокировка пользователя)
  }
}

// Экспортируем singleton instance
export const securityMonitoring = new SecurityMonitoringService();

// Вспомогательные функции для быстрого использования
export const logFailedLogin = (email: string, error: string, metadata?: Record<string, unknown>) => 
  securityMonitoring.logFailedLogin(email, error, metadata);

export const logSuccessfulLogin = (userId: string, email: string, metadata?: Record<string, unknown>) => 
  securityMonitoring.logSuccessfulLogin(userId, email, metadata);

export const logRateLimitExceeded = (identifier: string, action: string, metadata?: Record<string, unknown>) => 
  securityMonitoring.logRateLimitExceeded(identifier, action, metadata);

export const logUnauthorizedAccess = (userId: string, resource: string, metadata?: Record<string, unknown>) => 
  securityMonitoring.logUnauthorizedAccess(userId, resource, metadata);

export const logCriticalError = (error: string, context: string, metadata?: Record<string, unknown>) => 
  securityMonitoring.logCriticalError(error, context, metadata);
